import type {
  DemographicsData,
  DemographicsSummary,
  ChartItem,
  DemographicsDisplayConfig,
  AgeDistributionEntry,
  GenderDistributionEntry
} from '@/types/demographics';

// Helper function to safely process percentage values
export const safeProcessPercentage = (value: number | null | undefined): number => {
  if (value == null || isNaN(value) || value < 0) return 0;
  return parseFloat(value.toFixed(1));
};

// Helper function to validate and process distribution data
export const processDistributionData = <T extends { key: string; value: number; name: string; fill: string }>(
  rawData: any[] | null | undefined,
  validator: (item: any) => boolean,
  mapper: (item: any) => T,
  colorMap: Record<string, string>,
  unknownKey: string,
  unknownName: string,
): T[] => {
  if (!rawData?.length) return [];

  try {
    const processedData = rawData.filter(validator).map(mapper);

    // Calculate total percentage
    const totalPercentage = processedData.reduce((sum, item) => sum + item.value, 0);

    // Handle remainder if total is not 100%
    if (totalPercentage < 99.9 && totalPercentage >= 0) {
      const remainderPercentage = safeProcessPercentage(100.0 - totalPercentage);

      if (remainderPercentage > 0.0) {
        const unknownIndex = processedData.findIndex((item) => item.key === unknownKey);

        if (unknownIndex !== -1) {
          processedData[unknownIndex].value = safeProcessPercentage(
            processedData[unknownIndex].value + remainderPercentage,
          );
        } else {
          processedData.push({
            key: unknownKey,
            name: unknownName,
            value: remainderPercentage,
            fill: colorMap[unknownKey] || colorMap.UNKNOWN_DEFAULT,
          } as T);
        }
      }
    }

    return processedData.sort((a, b) => b.value - a.value);
  } catch (error) {
    // Log error for debugging in development only
    if (process.env.NODE_ENV === 'development') {
      console.error('Error processing distribution data:', error);
    }
    return [];
  }
};

// Generic function to process age distribution data
export const processAgeDistributionData = (
  ageDistribution: AgeDistributionEntry[] | undefined,
  displayConfig: DemographicsDisplayConfig,
): ChartItem[] => {
  if (!ageDistribution?.length) return [];

  return processDistributionData<ChartItem>(
    ageDistribution,
    (item): item is AgeDistributionEntry =>
      item != null && item.ageBucket != null && item.percentage != null,
    (item) => ({
      key: item.ageBucket,
      name: displayConfig.ageBuckets[item.ageBucket]?.displayName || item.ageBucket,
      value: safeProcessPercentage(item.percentage),
      fill: displayConfig.ageBuckets[item.ageBucket]?.color || displayConfig.unknownValues.ageBucket.color,
    }),
    Object.fromEntries(
      Object.entries(displayConfig.ageBuckets).map(([key, config]) => [key, config.color])
    ),
    'UNKNOWN',
    displayConfig.unknownValues.ageBucket.displayName,
  );
};

// Generic function to process gender distribution data
export const processGenderDistributionData = (
  genderDistribution: GenderDistributionEntry[] | undefined,
  displayConfig: DemographicsDisplayConfig,
): ChartItem[] => {
  if (!genderDistribution?.length) return [];

  return processDistributionData<ChartItem>(
    genderDistribution,
    (item): item is GenderDistributionEntry =>
      item != null && item.gender != null && item.percentage != null && item.percentage > 0,
    (item) => ({
      key: item.gender,
      name: displayConfig.genders[item.gender]?.displayName || item.gender,
      value: safeProcessPercentage(item.percentage),
      fill: displayConfig.genders[item.gender]?.color || displayConfig.unknownValues.gender.color,
    }),
    Object.fromEntries(
      Object.entries(displayConfig.genders).map(([key, config]) => [key, config.color])
    ),
    'UNKNOWN',
    displayConfig.unknownValues.gender.displayName,
  );
};

// Generic function to extract summary data
export const extractDemographicsSummary = (
  demographicsData: DemographicsData,
  displayConfig: DemographicsDisplayConfig,
): DemographicsSummary => {
  try {
    const { ageDistribution = [], genderDistribution = [], topCountries = [], topCities = [] } = demographicsData;

    const topAgeBucket = ageDistribution?.reduce((max, current) => {
      return current.percentage > (max?.percentage ?? 0) ? current : max;
    });

    const ageBucket = topAgeBucket?.ageBucket
      ? displayConfig.ageBuckets[topAgeBucket.ageBucket]?.displayName || topAgeBucket.ageBucket
      : '--';

    const topGender = genderDistribution?.reduce((max, current) => {
      return current.percentage > (max?.percentage ?? 0) ? current : max;
    });

    const gender = topGender?.gender
      ? displayConfig.genders[topGender.gender]?.displayName || topGender.gender.toLowerCase()
      : '--';

    const topCountry = topCountries?.reduce((max, current) => {
      return current.percentage > (max?.percentage ?? 0) ? current : max;
    });
    const country = topCountry?.name || '--';

    const topCity = topCities?.reduce((max, current) => {
      return current.percentage > (max?.percentage ?? 0) ? current : max;
    });
    const city = topCity?.name || '--';

    return { ageBucket, gender, country, city };
  } catch (error) {
    console.error('Error processing basic demographics:', error);
    return { ageBucket: '--', gender: '--', country: '--', city: '--' };
  }
};
